import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import IntakeForm from "@/components/IntakeForm";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { ClipboardList, Plus, Search, ExternalLink, ArrowRightCircle, Trash2, Copy, Pencil } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { v: "all", l: "All" },
  { v: "new", l: "New" },
  { v: "in_progress", l: "In progress" },
  { v: "converted", l: "Converted" },
  { v: "rejected", l: "Rejected" },
];

const STATUS_STYLE = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  converted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-gray-100 text-gray-500 border-gray-200",
};
const statusLabel = (s) => ({ new: "New", in_progress: "In progress", converted: "Converted", rejected: "Rejected" }[s] || s);

export default function Intakes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [intakes, setIntakes] = useState([]);
  const [meta, setMeta] = useState({ case_types: [], contact_methods: [], statuses: [] });
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (tab !== "all") params.status = tab;
    if (search.trim()) params.search = search.trim();
    api.get("/intakes", { params }).then((r) => setIntakes(r.data)).catch(() => {});
  }, [tab, search]);

  useEffect(() => { api.get("/intake/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (intake) => { setEditing(intake); setFormOpen(true); };

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editing?.id) {
        await api.put(`/intakes/${editing.id}`, values);
        toast.success("Intake updated");
      } else {
        await api.post("/intakes", values);
        toast.success("Intake created");
      }
      setFormOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save intake");
    } finally {
      setSubmitting(false);
    }
  };

  const onStatus = async (intake, status) => {
    try { await api.patch(`/intakes/${intake.id}/status`, { status }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update status"); }
  };

  const confirmConvert = async () => {
    const intake = convertTarget;
    setConvertTarget(null);
    try {
      const { data } = await api.post(`/intakes/${intake.id}/convert`);
      toast.success(`Converted to client ${data.client?.client_id || ""}`);
      load();
      if (data.client_id) navigate(`/clients/${data.client_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not convert intake");
    }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/intakes/${deleteTarget.id}`); toast.success("Intake deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  const publicUrl = `${window.location.origin}/public/intake`;
  const copyPublicUrl = () => {
    navigator.clipboard?.writeText(publicUrl).then(
      () => toast.success("Public intake link copied"),
      () => toast.error("Could not copy link"),
    );
  };

  return (
    <div className="p-6 lg:p-8" data-testid="intakes-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-[#002FA7]" /> Intake
          </h1>
          <p className="text-sm text-gray-500 mt-1">{intakes.length} intake{intakes.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyPublicUrl} data-testid="copy-public-intake-link"
            className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-sm font-semibold text-sm">
            <Copy className="w-4 h-4" /> Public link
          </button>
          <button onClick={openNew} data-testid="new-intake-button"
            className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm">
            <Plus className="w-4 h-4" /> New intake
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-sm flex-wrap">
          {TABS.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`intake-tab-${t.v}`}
              className={`px-3 py-1.5 rounded-sm text-sm font-semibold transition-colors ${tab === t.v ? "bg-white text-[#002FA7] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              {t.l}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="intake-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, case type…"
            className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-sm w-72 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </div>
      </div>

      {intakes.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-16 border border-dashed border-gray-200 rounded-sm" data-testid="intakes-empty">
          No intakes here yet. Create one or share the public intake link.
        </div>
      ) : (
        <div className="space-y-2" data-testid="intakes-list">
          {intakes.map((it) => {
            const name = `${it.first_name || ""} ${it.last_name || ""}`.trim() || "Unnamed intake";
            return (
              <div key={it.id} data-testid={`intake-row-${it.id}`}
                className="bg-white border border-gray-200 rounded-sm p-4 flex items-start justify-between gap-4 hover:border-gray-300 transition-colors">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{name}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_STYLE[it.status] || ""}`} data-testid={`intake-status-${it.id}`}>
                      {statusLabel(it.status)}
                    </span>
                    {it.source === "public_form" && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">PUBLIC</span>}
                  </div>
                  <p className="text-sm text-gray-500 mt-1 truncate">
                    {[it.case_type, it.email, it.phone].filter(Boolean).join(" · ") || "No details"}
                  </p>
                  {it.incident_description && <p className="text-xs text-gray-400 mt-1 line-clamp-1">{it.incident_description}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <select value={it.status} onChange={(e) => onStatus(it, e.target.value)}
                    data-testid={`intake-status-select-${it.id}`} disabled={it.status === "converted"}
                    className="text-xs border border-gray-300 rounded-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:opacity-60">
                    {(meta.statuses.length ? meta.statuses : ["new", "in_progress", "converted", "rejected"]).map((s) => (
                      <option key={s} value={s} disabled={s === "converted"}>{statusLabel(s)}</option>
                    ))}
                  </select>
                  {it.converted_client_id ? (
                    <button onClick={() => navigate(`/clients/${it.converted_client_id}`)} data-testid={`intake-view-client-${it.id}`}
                      className="flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline px-2 py-1.5">
                      <ExternalLink className="w-3.5 h-3.5" /> Client
                    </button>
                  ) : (
                    <button onClick={() => setConvertTarget(it)} data-testid={`intake-convert-${it.id}`}
                      className="flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:bg-[#002FA7]/5 rounded-sm px-2 py-1.5">
                      <ArrowRightCircle className="w-4 h-4" /> Convert
                    </button>
                  )}
                  <button onClick={() => openEdit(it)} data-testid={`intake-edit-${it.id}`}
                    className="text-gray-400 hover:text-[#002FA7] p-1.5"><Pencil className="w-4 h-4" /></button>
                  {user?.role === "admin" && (
                    <button onClick={() => setDeleteTarget(it)} data-testid={`intake-delete-${it.id}`}
                      className="text-gray-400 hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto bg-[#F9FAFB]" data-testid="intake-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit intake" : "New intake"}</DialogTitle>
            <DialogDescription>Capture the lead's contact, case and medical details.</DialogDescription>
          </DialogHeader>
          <IntakeForm
            initial={editing}
            caseTypes={meta.case_types}
            contactMethods={meta.contact_methods}
            onSubmit={onSubmit}
            submitting={submitting}
            submitLabel={editing ? "Save changes" : "Create intake"}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(convertTarget)} onOpenChange={(o) => !o && setConvertTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="convert-intake-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Convert to client?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new client record from <strong>{`${convertTarget?.first_name || ""} ${convertTarget?.last_name || ""}`.trim()}</strong> and mark the intake as converted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmConvert} data-testid="confirm-convert-intake-button" className="bg-[#002FA7] hover:bg-[#002480] rounded-sm">Convert</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-intake-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete intake?</AlertDialogTitle>
            <AlertDialogDescription>This intake will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-intake-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
